import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate
} from "react-router-dom";
import Layouts from "../Views/Common/Layout";
import StudentLayout from "../Views/StudentSection/Common/Layout";
import constants from '../Constants/constants';
import lazyWithRetry, { AdminRoute, StudentRoute, RoleProtectedRoute } from './helper';
import NewGPAHistory from '../Views/GPA/NewGPAHistory';

const ClinicalSettings = lazyWithRetry(() => import('../ClinicalViews/Settings/Clinical'));
const ClearancesSettings = lazyWithRetry(() => import('../ClinicalViews/Settings/Clearances'));
const ObservationsSettings = lazyWithRetry(() => import('../ClinicalViews/Settings/Observations'));
const ReportListing = lazyWithRetry(() => import('../ClinicalViews/Reports/Listing'));
const ObservationRequirements = lazyWithRetry(() => import('../ClinicalViews/Observations/ObservationRequirementsInstructions'));
const ObservationHours = lazyWithRetry(() => import('../ClinicalViews/Observations/ObservationHours'));
const ObservationVerificationLetter = lazyWithRetry(() => import('../ClinicalViews/Observations/ObservationVerificationLetter'));
const StudentsListing = lazyWithRetry(() => import('../ClinicalViews/Students/Listing'));
const PendingList = lazyWithRetry(() => import('../ClinicalViews/Students/PendingListing'));

const Login = lazyWithRetry(() => import('../Views/Common/Login'));
const AssignAdvisors = lazyWithRetry(() => import('../Views/Advising/AssignAdvisors'));
const AddSchedule = lazyWithRetry(() => import('../Views/Advising/AddSchedule'));
const NotificationListing = lazyWithRetry(() => import('../Views/Notifications/Listing'));
const AddNotifications = lazyWithRetry(() => import('../Views/Notifications/AddNotificatons'));
const ActivityLogs = lazyWithRetry(() => import('../Views/ActivityLogs'));
const FacultyListing = lazyWithRetry(() => import('../Views/Faculty/FacultyListing'));
const FacultyRegistration = lazyWithRetry(() => import('../Views/Faculty/FacultyRegistration'));
const FacultyEdit = lazyWithRetry(() => import('../Views/Faculty/FacultyEdit'));
const SendEmails = lazyWithRetry(() => import('../Views/Mails/SendEmails'));
const EmailReports = lazyWithRetry(() => import('../Views/Mails/EmailReports'));
const StudentListing = lazyWithRetry(() => import('../Views/Students/StudentListing'));
const AdvisingDocs = lazyWithRetry(() => import('../Views/Advising/AdvisingDocs'));
const EditStudent = lazyWithRetry(() => import('../Views/Students/EditStudent'));
const AcademicSpecification = lazyWithRetry(() => import('../Views/GPA/AcademicSpecification'));
const StudentGpa = lazyWithRetry(() => import('../Views/GPA/StudentGpa'));
const DissmissedList = lazyWithRetry(() => import('../Views/GPA/DissmissedList'));
const GpaHistory = lazyWithRetry(() => import('../Views/GPA/GpaHistory'));
const AssigningMentor = lazyWithRetry(() => import('../Views/AssigningMentors/AssigningMentor'));
const UserRole = lazyWithRetry(() => import('../Views/UserRoles/UserRole'));
const StudentInfoTabs = lazyWithRetry(() => import('../Views/Students/StudentInfoTabs'));
const ManageSurveys = lazyWithRetry(() => import('../Views/Surveys/ManageSurveys/ManageSurveys'));
const ChangePassword = lazyWithRetry(() => import('../Views/Common/ChangePassword'));
const ForgotPassword = lazyWithRetry(() => import('../Views/Common/ForgotPassword'));
const SurveyTypes = lazyWithRetry(() => import('../Views/Surveys/SurveyTypes/SurveyTypes'));
const Registration = lazyWithRetry(() => import('../Views/Students/Registration'));
const Questions = lazyWithRetry(() => import('../Views/Surveys/ManageSurveys/Questions'));
const SurveyResult = lazyWithRetry(() => import('../Views/Surveys/ManageSurveys/SurveyResult'));
const SurveyReport = lazyWithRetry(() => import('../Views/Surveys/ManageSurveys/SurveyReport'));
const AreaCourses = lazyWithRetry(() => import('../Views/Courses/ManageCourses/AreaCourses'));
const SLPCourses = lazyWithRetry(() => import('../Views/Courses/ManageCourses/SLPCourses'));
const FreeElectives = lazyWithRetry(() => import('../Views/Courses/ManageCourses/FreeElectives'));
const Assessments = lazyWithRetry(() => import('../Views/Courses/ManageCourses/Assessments'));
const FacultyCoursesAssignment = lazyWithRetry(() => import('../Views/Courses/FacultyCoursesAssignment'));
const StudentCourseList = lazyWithRetry(() => import('../Views/Courses/StudentCourseList'));
const Courses = lazyWithRetry(() => import('../Views/Courses/Courses'));
const ResetPassword = lazyWithRetry(() => import('../Views/Common/ResetPassword'));
const ConfidentialNotes = lazyWithRetry(() => import('../Views/Students/ConfidentialNotes'));
const StudentImport = lazyWithRetry(() => import('../Views/StudentImports/StudentImport'));
const FailedImport = lazyWithRetry(() => import('../Views/StudentImports/FailedImport'));
const AdvisorSignup = lazyWithRetry(() => import('../Views/Advising/AdvisorSignup'));
const StudentAdvisorSignup = lazyWithRetry(() => import('../Views/StudentSection/StudentAdvisorSignup'));
const StudentProfile = lazyWithRetry(() => import('../Views/StudentSection/StudentProfile.js/StudentProfile'));
const CreateSemester = lazyWithRetry(() => import('../Views/CreateSemester'));
const StudentNotes = lazyWithRetry(() => import('../Views/StudentSection/StudentNotes'));
const Notifications = lazyWithRetry(() => import('../Views/Notifications/Notifications'));
const MyCourses = lazyWithRetry(() => import('../Views/Courses/MyCourses'));
const CoursePlanNotes = lazyWithRetry(() => import('../Views/Students/CoursePlanNotes'));
const Clearances = lazyWithRetry(() => import('../Views/Clinical/Clearance'));
const StudentRelatedNotes = lazyWithRetry(() => import('../Views/Advising/StudentRelatedNotes'));
const StudentCourses = lazyWithRetry(() => import('../Views/Students/StudentCourses/Courses'));
const LinkExpired = lazyWithRetry(() => import('../Views/Common/LinkExpired'));
const StudentCheckList = lazyWithRetry(() => import('../Views/Students/Checklist'));
const StudentSemesterCourses = lazyWithRetry(() => import('../Views/StudentSection/StudentSemesterCourses'));
const StudentStudyPlan = lazyWithRetry(() => import('../Views/StudentSection/StudentStudyPlan'));
const SurveyList = lazyWithRetry(() => import('../Views/Surveys/Common/SurveyList'));
const AnswerSurvey = lazyWithRetry(() => import('../Views/Surveys/Common/AnswerSurvey'));
const LearningOutcomesList = lazyWithRetry(() => import('../Views/Surveys/Common/LearningOutcomesList'));
const MentorDetailsResults = lazyWithRetry(() => import('../Views/Surveys/ManageSurveys/MentorResultsDetails'));
const PageNotFound = lazyWithRetry(() => import('../Views/PageNotFound'));
const ClearCachePage = lazyWithRetry(() => import('../Views/ClearCachePage'));
const ActiveCourseList = lazyWithRetry(() => import('../Views/Courses/ActiveCourseList'));
const PendingCoursesReport = lazyWithRetry(() => import('../Views/Courses/PendingCoursesReport'));
const StudentDashboard = lazyWithRetry(() => import('../Views/StudentSection/StudentDashboard'));
const Dashboard = lazyWithRetry(() => import('../Views/Dashboard'));

  //'ROLES', [1 => 'Admin', 2 => 'Faculty', 3 => 'Department Head', 4 => 'Adjunct faculty', 5 => 'Clinical Supervisor']);
  const routerBrowser = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/student-login" />} />
        <Route path="/login" element={<Navigate to="/student-login" />} />
        <Route path="/student" element={<Navigate to="/student-login" />} />
        <Route path="/student-login" element={<Login type="student" />} />
        <Route path="/staff" element={<Navigate to="/staff-login" />} />
        <Route path="/staff-login" element={<Login type="staff" />} />
        <Route exact path="/request-password-reset" element={<ForgotPassword />} />
        <Route exact path="/reset-password" element={<ResetPassword/>} />
        <Route exact path='/link-expired' element={<LinkExpired/>}/>
        {/* Protected Routes for Students */}
        <Route element={<StudentRoute><StudentLayout /></StudentRoute>}>
          {/* Add more student-specific routes here */}
          <Route exact path="/student-change-password" element={<ChangePassword />} />
          <Route exact path="/student-course-plan-notes" element={<CoursePlanNotes />} />
          <Route exact path="/student-dashboard" element={<StudentDashboard />} />
          <Route exact path="/student-advisor-signup" element={<StudentAdvisorSignup/>}/>
          <Route exact path='student-profile' element={<StudentProfile/>}/>
          <Route exact path="/student-notifications" element={<Notifications/>}/>
          <Route exact path="/student-courses" element={<StudentCourses />}/>
          <Route exact path="/student-checklist" element={<StudentCheckList />}/>
          <Route exact path="/semester-courses" element={<StudentSemesterCourses/>}/>
          <Route exact path="/plan-of-study" element={<StudentStudyPlan/>}/>
          <Route exact path="/all-surveys" element={<SurveyList type={constants.Student} />}/>
          <Route exact path="/answer-survey-student" element={<AnswerSurvey />}/>
          <Route exact path="/student-notes" element={<StudentNotes/>}/>
          <Route exact path="/clinical-student-clearances" element={<StudentInfoTabs />} />
          <Route exact path="/clinical-observations-hours" element={<ObservationHours showBreadcrumbs={true} />} />
          <Route exact path="/clinical-observation-requirements" element={<ObservationRequirements />} />
          <Route exact path="/clinical-verification-letter" element={<ObservationVerificationLetter showBreadcrumbs={true} />}/>
        </Route>
        {/* Protected Routes for Admins with Admin Layout */}
        <Route element={<AdminRoute><Layouts /></AdminRoute>}>
          {/* Add more admin-specific routes here */}
          <Route path="/dashboard" element={<Dashboard />}/>
          <Route exact path="/create-semester" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <CreateSemester/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/assigning-advisors" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <AssignAdvisors/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/advising-documents" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <AdvisingDocs/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/add-schedule" element={
            <RoleProtectedRoute allowedRoles={["1","3","6"]}>
              <AddSchedule />
            </RoleProtectedRoute>}
          />
          <Route exact path="/list-schedule" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <AddSchedule />
            </RoleProtectedRoute>}
          />
          <Route exact path="/notification-list" element={
            <RoleProtectedRoute allowedRoles={["1","3","6"]}>
              <NotificationListing />
            </RoleProtectedRoute>}
          />
          <Route exact path="/notification-add" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","6"]}>
              <AddNotifications />
            </RoleProtectedRoute>}
          />
          <Route exact path="/activity-logs" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <ActivityLogs/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/staff-list" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <FacultyListing />
            </RoleProtectedRoute>}
          />
          <Route exact path="/staff-registration" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <FacultyRegistration/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/student-listing" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <StudentListing />
            </RoleProtectedRoute>}
          />
          <Route exact path="/course-plan-notes" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <CoursePlanNotes />
            </RoleProtectedRoute>}
          />
          <Route exact path="/change-password" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <ChangePassword />
            </RoleProtectedRoute>}
          />
          <Route exact path="/staff-edit" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <FacultyEdit/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/send-emails" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <SendEmails/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/email-reports" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <EmailReports/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/edit-student-details" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <EditStudent />
            </RoleProtectedRoute>}
          />
          <Route exact path="/academic-specification" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <AcademicSpecification />
            </RoleProtectedRoute>}
          />
          <Route exact path="/student-gpa" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <StudentGpa />
            </RoleProtectedRoute>}
          />
          <Route exact path="/dismissed-list" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <DissmissedList />
            </RoleProtectedRoute>}
          />
          <Route exact path="/gpa-history" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <NewGPAHistory />
            </RoleProtectedRoute>}
          />
          <Route exact path="/assign-mentors" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <AssigningMentor />
            </RoleProtectedRoute>}
          />
          <Route exact path="/user-roles" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <UserRole />
            </RoleProtectedRoute>}
          />
          <Route exact path="/student-information" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <StudentInfoTabs />
            </RoleProtectedRoute>}
          />
          <Route exact path="/manage-surveys" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <ManageSurveys />
            </RoleProtectedRoute>}
          />
          <Route exact path="/survey-types" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <SurveyTypes />
            </RoleProtectedRoute>}
          />
          <Route exact path="/student-registration" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <Registration />
            </RoleProtectedRoute>}
          />
          <Route exact path="/questions" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <Questions />
            </RoleProtectedRoute>}
          />
          <Route exact path="/survey-result" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <SurveyResult />
            </RoleProtectedRoute>}
          />
          <Route exact path="/surveys-report" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <SurveyReport />
            </RoleProtectedRoute>}
          />
          <Route exact path="/results-details" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <MentorDetailsResults />
            </RoleProtectedRoute>}
          />
          <Route exact path="/student-course-list" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <StudentCourseList/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/courses" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <Courses/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/pending-courses-report" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <PendingCoursesReport />
            </RoleProtectedRoute>}
          />
          <Route exact path="/faculty-courses-assignment" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <FacultyCoursesAssignment/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/faculty-courses-assignment-history" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <FacultyCoursesAssignment/>
              </RoleProtectedRoute>}
          />
          <Route exact path="/areas-courses" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <AreaCourses/>
              </RoleProtectedRoute>}
          />
          <Route exact path="/slp-courses" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <SLPCourses/>
              </RoleProtectedRoute>}
          />
          <Route exact path="/free-electives" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <FreeElectives/>
              </RoleProtectedRoute>}
          />
          <Route exact path="/assessments" element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <Assessments/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/confidential-notes" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <ConfidentialNotes/>
            </RoleProtectedRoute>}
          />
          <Route exact path='student-import' element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <StudentImport/>
            </RoleProtectedRoute>}
          />
          <Route exact path='failed-import' element={
            <RoleProtectedRoute allowedRoles={["1","3"]}>
              <FailedImport/>
            </RoleProtectedRoute>}
            />
          <Route exact path="/advisor-signup" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","6"]}>
              <AdvisorSignup/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/profile" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <FacultyEdit/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/notifications" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <Notifications/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/my-courses" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <MyCourses/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/clearances" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <Clearances />
            </RoleProtectedRoute>}
          />
          <Route exact path="/student-related-notes" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <StudentRelatedNotes/>
            </RoleProtectedRoute>}
          />
          <Route exact path="/my-surveys" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <SurveyList type={constants.Faculty} />
            </RoleProtectedRoute>}
          />
          <Route exact path="/learning-outcomes" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <LearningOutcomesList type={constants.Faculty} />
            </RoleProtectedRoute>}
          />
          <Route exact path="/answer-survey" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5","6"]}>
              <AnswerSurvey />
            </RoleProtectedRoute>}
          />
          <Route exact path="/active-course-list" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","4","5"]}>
              <ActiveCourseList />
            </RoleProtectedRoute>}
          />
          {/* CLINICAL ROUTES START*/}
          <Route exact path="/student-clinics-list" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <StudentsListing />
            </RoleProtectedRoute>}
          />
          <Route exact path="/pending-uploads-list" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <PendingList />
            </RoleProtectedRoute>}
          />
          <Route exact path="/clinical-clinics-settings" element={
            <RoleProtectedRoute allowedRoles={["1","3","6"]}>
              <ClinicalSettings />
            </RoleProtectedRoute>}
          />
          <Route exact path="/clinical-clearances-settings" element={
            <RoleProtectedRoute allowedRoles={["1","3","6"]}>
              <ClearancesSettings />
            </RoleProtectedRoute>}
          />
          <Route exact path="/clinical-observations-settings" element={
            <RoleProtectedRoute allowedRoles={["1","3","6"]}>
              <ObservationsSettings />
            </RoleProtectedRoute>}
          />
          <Route exact path="/clinical-report-listing" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <ReportListing />
            </RoleProtectedRoute>}
          />
           <Route exact path="/clinical-verification-letter-edit" element={
            <RoleProtectedRoute allowedRoles={["1","2","3","5","6"]}>
              <ObservationVerificationLetter showBreadcrumbs={true} />
            </RoleProtectedRoute>}
          />

          {/* CLINICAL ROUTES END */}
        </Route>
        <Route exact path="*" element={<PageNotFound />}/>
        <Route exact path="*" element={<ClearCachePage />}/>
    </>
    ),
    {
      basename: constants.BaseUrl,
    }
  );
export default routerBrowser;